/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PlanBadge, tokens, Link, Button, Loader, Badge, BasePopover } from '@hotjar/hotsauce';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { Checkbox } from '@hotjar/webapp/components/Checkbox';

import { Icon } from '@hotjar/webapp/components/Icon';
import { Pillar } from '@hotjar/webapp/components/Pillar';

import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useSubscription } from '@hotjar/webapp/hooks/useSubscription';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { validateUrl, validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { useGate, useTierForGate } from '@hotjar/allowances';

import type { Site } from '@hotjar/site';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { useAccountTrialInfo } from '@hotjar/account';
import { bus, refreshData } from '@hotjar/webapp/utils/events';
import { useHelpHotjarUrl } from '@hotjar/translations';
import {
  Section,
  Paragraph,
  Link as LegacyLink,
  Separator,
  Label,
  Description,
  StyledSelect as Select,
  StyledInput as Input,
} from '../styles';
import { sessionsSettingsApi } from '../api';
import type { FormTargetingRule, SessionsSettings, SessionTargetingRule } from '../types';

// constants and helpers
const HOTJAR_CAPTURE_TRAFFIC_LINK = '/articles/115011624047';
const CUSTOM_EVENT_LINK = '/articles/115015712548';
const TRACKING_URL_CHANGES_LINK = '/articles/115011805428';
const ALLOWED_IFRAMES_DOMAINS_LINK = '/articles/115011624347';
const MATCHING_OPTIONS_LINK = '/articles/115014827527';

const urlChangesOptions: Array<string> = ['automatic', 'automatic_with_fragments', 'manual'];

const defaultTargetingRule: SessionTargetingRule = {
  component: 'url',
  match_operation: 'simple',
  negate: false,
  pattern: '',
};

const matchOperations: Array<string> = [
  'simple',
  'exact',
  'starts_with',
  'ends_with',
  'contains',
  'regex',
];

const PLUS_ICON_NAME = 'plus';

// API helpers
// Transforms {component: 'trigger', match_operation: 'exact'} into  {match_operation: 'trigger'}
const getTargetingRulesApiToForm = (rules: Array<SessionTargetingRule>): Array<FormTargetingRule> =>
  rules.map((rule) => {
    return {
      pattern: rule.pattern,
      match_operation: rule.component === 'url' ? rule.match_operation : 'trigger',
    };
  });

// Transforms {match_operation: 'trigger'} into {component: 'trigger', match_operation: 'exact'}
const getTargetingRulesFormToApi = (rules: Array<FormTargetingRule>): Array<SessionTargetingRule> =>
  rules.map((rule) => {
    return {
      match_operation: rule.match_operation === 'trigger' ? 'exact' : rule.match_operation,
      component: rule.match_operation === 'trigger' ? 'trigger' : 'url',
      pattern: rule.pattern,
      negate: false,
    };
  });

// Styles
const InputsCss = css`
  margin-top: 8px;
  margin-bottom: 32px;
  text-transform: none;
  font-weight: normal;
`;

const DomainInputContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const TargetingRulesContainer = styled.div`
  margin-left: ${tokens.spacer.x3};
  margin-top: ${tokens.spacer.x2};
`;

interface TargetingRuleSeparatorProps {
  separator: string;
}
const TargetingRuleSeparator = styled.hr`
  display: block;
  overflow: visible;
  position: relative;
  margin: ${tokens.spacer.x2} 0 ${tokens.spacer.x2} ${tokens.spacer.x3};
  border-top: 1px dashed ${tokens.color.ui_border};
  &:before {
    position: absolute;
    content: '${({ separator }: TargetingRuleSeparatorProps) => separator}';
    left: -24px;
    top: -9px;
    color: ${tokens.color.ui_border};
    font-size: 12px;
  }
`;

const RuleContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;
interface RuleActionProps {
  disabled: boolean;
}

const RuleAction = styled.div`
  height: 40px;
  width: 32px;
  line-height: 40px;
  text-align: center;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${({ disabled }: RuleActionProps) => (disabled ? 0.8 : 1)};
`;

const TargetingRuleError = styled.div`
  color: ${tokens.color.text_danger};
  margin-top: 4px;
  font-size: 12px;
`;

const HelpLinkContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${tokens.color.text_light};
  margin-top: ${tokens.spacer.x2};
`;

const StyledPlanBadge = styled(PlanBadge)`
  cursor: pointer;
  margin-left: 2px;
`;

const InternalPopover = styled.div`
  white-space: pre-line;
  & strong {
    font-weight: 500;
  }
`;

// interfaces
interface SessionTargetingTrackingProps {
  site: Site;
  onFormDirty: (isDirty: boolean) => void;
}

type FormResult = {
  session_include_short: boolean;
  session_targeting_rules_present: boolean;
  session_targeting_rules: Array<FormTargetingRule>;
  state_change_listen_mode: SessionsSettings['state_change_listen_mode'];
  allowed_iframe_domains: Array<{ url: string }>;
  session_capture_console_consent: boolean;
};

// component
export const SessionTargetingTracking = ({
  site,
  onFormDirty,
}: SessionTargetingTrackingProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { click } = useTrack();
  const gateContext = {
    id: String(site.id),
    type: 'site',
  } as const;
  const [isLoading, setIsLoading] = useState(true);
  const canUsePageTargeting = useGate('analytics.page_targeting', gateContext);
  const pageTargetingTier = useTierForGate('analytics.page_targeting', gateContext);
  const canUseJavaScriptTriggers = useGate('core.targeting_triggers', gateContext);
  const { upsell } = useSubscription();

  const { success, error } = useNotify();

  const { register, control, handleSubmit, reset, formState, watch, errors } = useForm<FormResult>({
    mode: 'onBlur',
  });

  const { hasPermission } = useAuthorization();
  const hasSessionSettingsUpdatePermission = hasPermission(
    'site',
    `${site.id}`,
    'session-settings-update',
  );

  // domains list working as a field array in order to have them in the form
  const {
    fields: domainsFields,
    append: domainsAppend,
    remove: domainsRemove,
  } = useFieldArray({
    control,
    name: 'allowed_iframe_domains',
  });

  // targeting options
  const {
    fields: targetingRuleFields,
    append: targetingRuleAppend,
    remove: TargetingRuleRemove,
  } = useFieldArray({
    control,
    name: 'session_targeting_rules',
  });

  const { isDirty, isSubmitting } = formState;
  const [canSubmit, setCanSubmit] = useState(true);

  // Default to false and this will get updated when we fetch the settings,
  // this way its not confusing by potentially allowing the settings to be
  // toggled before it has a chance to disable itself.
  const [canToggleSessionIncludeShort, setCanToggleSessionIncludeShort] = useState(false);

  // domain input & button
  const [addDomainError, setAddDomainError] = useState(false);
  const [domainToAdd, setDomainToAdd] = useState('');
  const addDomainButtonRef = useRef<HTMLButtonElement>(null);

  const watchTargetingRulesPresent = watch('session_targeting_rules_present');
  const watchTargetingRules = watch('session_targeting_rules');

  // methods
  const trackClick = (elementName: string) => {
    click(elementName, 'site.settings_targeting', {
      account_id: site.account.id,
      organization_id: site.organization.id,
      site_id: site.id,
      state_event_was_triggered_on: 'site.settings',
      url_event_was_triggered_on: `/site/${site.id}/settings#targeting`,
    });
  };

  // domain specific methods
  const verifyDomainToAdd = (): boolean => {
    if (domainToAdd.length > 0 && !validateUrl(domainToAdd, false)) {
      setAddDomainError(true);
      return false;
    }

    setAddDomainError(false);
    return true;
  };

  const onDomainToAddChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDomain = event.target.value;
    setDomainToAdd(newDomain);
  };

  const onAddDomain = () => {
    domainsAppend({ url: domainToAdd });
    setDomainToAdd('');
  };

  const onDomainToAddKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (verifyDomainToAdd()) addDomainButtonRef.current?.click();
    }
  };

  useEffect(() => {
    if (canUseJavaScriptTriggers) {
      matchOperations.push('trigger');
    }
  }, [canUseJavaScriptTriggers]);

  useEffect(() => {
    onFormDirty(isDirty);
  }, [isDirty, onFormDirty]);

  useEffect(() => {
    if (!isLoading && targetingRuleFields.length === 0 && watchTargetingRulesPresent) {
      targetingRuleAppend(defaultTargetingRule);
    }
  }, [watchTargetingRulesPresent, targetingRuleFields, targetingRuleAppend, isLoading]);

  useEffect(() => {
    // get initial state
    (async () => {
      setIsLoading(true);
      const response = await sessionsSettingsApi.get(site.id);
      if (response.success) {
        // minimum loading time
        reset({
          session_include_short: !response.data?.session_exclude_short,
          session_targeting_rules_present: !!response.data?.session_targeting_rules?.length,
          session_targeting_rules: getTargetingRulesApiToForm(
            response.data?.session_targeting_rules || [],
          ),
          state_change_listen_mode: response.data?.state_change_listen_mode,
          allowed_iframe_domains: (response.data?.allowed_iframe_domains || []).map((domain) => {
            return { url: domain };
          }),
          session_capture_console_consent: response.data?.session_capture_console_consent ?? false,
        });

        setCanToggleSessionIncludeShort(
          // Since this is a new value in the API response, it may not be
          // present so we choose the previous default of `true`
          response.data?.can_toggle_session_exclude_short ?? true,
        );

        setCanSubmit(true);

        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    })();
  }, [reset, site.id]);

  const isErrorCapturingFFEnabled = useFeatureFlag('error_reporting');

  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const trialInfo = useAccountTrialInfo();
  const planAllowsErrorCapturing =
    site &&
    trialInfo &&
    isSiteOnScaleOrScaleTrial({ ...site, trial: { active: isOnActiveOrEligibleTrial(trialInfo) } });

  const onSubmit = async (data: FormResult) => {
    setCanSubmit(false);
    const dataToSend: Partial<SessionsSettings> = {
      session_exclude_short: !data.session_include_short,
      session_targeting_rules: data.session_targeting_rules_present
        ? getTargetingRulesFormToApi(data.session_targeting_rules)
        : [],
      state_change_listen_mode: data.state_change_listen_mode,
      allowed_iframe_domains: domainsFields.map((domain) => domain.url),
      session_capture_console_consent: data.session_capture_console_consent,
    };

    const response = await sessionsSettingsApi.post(site.id, 'update_session_settings', dataToSend);

    const onSuccess = () => {
      success(t('common:site-settings.targeting-tracking.save-success'));
      // we reset to have a state as if we just loaded from the api
      reset({
        ...data,
        allowed_iframe_domains: domainsFields.map((domain) => {
          return { url: domain.url };
        }),
      });
      setCanSubmit(true);
    };

    if (response.success) {
      const hasRefreshDataListener = bus.emitter.listeners(refreshData.eventType).length > 0;

      if (hasRefreshDataListener) {
        bus.publish(refreshData(() => onSuccess()));
      } else {
        onSuccess();
      }
    } else {
      error(t('common:site-settings.targeting-tracking.save-error'));
      setCanSubmit(true);
    }
  };

  // loading
  if (isLoading) {
    return (
      <Loader
        isLoading
        css={css`
          margin: 50px auto;
        `}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Section>
        <Paragraph>
          <Trans i18nKey="common:site-settings.targeting-tracking.session-tracking-information">
            The settings below are only applicable to
            <LegacyLink
              href={`${helpHotjarUrl}${HOTJAR_CAPTURE_TRAFFIC_LINK}`}
              target="_blank"
              onClick={() => {
                trackClick('site.settings_targeting_potential-traffic-learn-more-link');
              }}
              rel="noreferrer"
            >
              potential traffic Hotjar could capture
            </LegacyLink>
            .
          </Trans>
        </Paragraph>

        <Separator />

        <Controller
          control={control}
          name="session_include_short"
          render={({ onChange, value }) => (
            <Checkbox
              partial={false}
              disabled={!hasSessionSettingsUpdatePermission || !canToggleSessionIncludeShort}
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
              labelCss={css`
                color: ${tokens.color.text_default};
                margin-bottom: ${tokens.spacer.x3};
              `}
            >
              {t('common:site-settings.targeting-tracking.short-sessions')}
            </Checkbox>
          )}
        />

        <Controller
          control={control}
          name="session_targeting_rules_present"
          render={({ onChange, value }) => (
            <Checkbox
              partial={false}
              disabled={!hasSessionSettingsUpdatePermission}
              checked={value}
              onChange={(e) => {
                if (!canUsePageTargeting) {
                  upsell('site.settings_targeting');
                } else {
                  onChange(e.target.checked);
                }
              }}
              labelCss={css`
                color: ${tokens.color.text_default};
                margin-bottom: ${tokens.spacer.x1};
              `}
            >
              <Trans i18nKey="common:site-settings.targeting-tracking.targeting-sessions">
                Start capturing sessions on specific pages or when a
                <Link
                  css={css`
                    color: inherit !important;
                  `}
                  kind="inline"
                  isExternal
                  href={`${helpHotjarUrl}${CUSTOM_EVENT_LINK}`}
                >
                  custom event
                </Link>
                fires.{' '}
              </Trans>
              {!canUsePageTargeting && pageTargetingTier && (
                <StyledPlanBadge kind={pageTargetingTier} size="s" />
              )}
            </Checkbox>
          )}
        />

        {watchTargetingRulesPresent && (
          <TargetingRulesContainer>
            {targetingRuleFields.map((rule, index) => {
              return (
                <React.Fragment key={rule.id}>
                  <RuleContainer data-testid={`targeting-rule-${index}`}>
                    <Select
                      name={`session_targeting_rules[${index}].match_operation`}
                      ref={register()}
                      defaultValue={rule.match_operation}
                      containerCss={css`
                        width: 200px;
                        flex-grow: 0;
                      `}
                      disabled={!hasSessionSettingsUpdatePermission}
                      css={css`
                        border-top-right-radius: 0;
                        border-bottom-right-radius: 0;
                      `}
                    >
                      {matchOperations.map((operation) => (
                        <option value={operation} key={`${rule.id}-${operation}`}>
                          {t(
                            `common:site-settings.targeting-tracking.match-operations.${operation}`,
                          )}
                        </option>
                      ))}
                    </Select>
                    <Input
                      name={`session_targeting_rules[${index}].pattern`}
                      inputRef={register({
                        validate: (value) => {
                          if (value === '') {
                            return t(
                              'common:site-settings.targeting-tracking.match-operation-empty-error',
                            ) as string;
                          }
                          if (
                            (watchTargetingRules[index]?.match_operation === 'simple' ||
                              watchTargetingRules[index]?.match_operation === 'exact') &&
                            !validateHTTPSUrl(value)
                          ) {
                            return t(
                              'common:site-settings.targeting-tracking.match-operation-invalid-error',
                            ) as string;
                          }
                          return undefined;
                        },
                      })}
                      defaultValue={rule.pattern}
                      placeholder={t(
                        `common:site-settings.targeting-tracking.match-operations.${
                          (watchTargetingRules && watchTargetingRules[index]?.match_operation) ||
                          rule.match_operation
                        }-placeholder`,
                      )}
                      isError={
                        !!(
                          errors.session_targeting_rules &&
                          errors.session_targeting_rules[index]?.pattern
                        )
                      }
                      containerCss={css`
                        width: 266px;
                        margin-right: ${tokens.spacer.x1};
                        flex-grow: 0;
                      `}
                      disabled={!hasSessionSettingsUpdatePermission}
                      css={css`
                        border-top-left-radius: 0;
                        border-bottom-left-radius: 0;
                      `}
                    />

                    {targetingRuleFields.length > 1 && (
                      <RuleAction
                        disabled={!hasSessionSettingsUpdatePermission}
                        onClick={() => {
                          if (hasSessionSettingsUpdatePermission) TargetingRuleRemove(index);
                        }}
                      >
                        <Icon size={16} icon="trash" color={tokens.color.neutral_600} />
                      </RuleAction>
                    )}
                    {targetingRuleFields.length === index + 1 && (
                      <RuleAction
                        disabled={!hasSessionSettingsUpdatePermission}
                        onClick={() => {
                          if (hasSessionSettingsUpdatePermission)
                            targetingRuleAppend(defaultTargetingRule);
                        }}
                      >
                        <Icon size={16} icon={PLUS_ICON_NAME} color={tokens.color.neutral_600} />
                      </RuleAction>
                    )}
                  </RuleContainer>

                  {errors.session_targeting_rules &&
                    !!errors.session_targeting_rules[index]?.pattern?.message && (
                      <TargetingRuleError>
                        {errors.session_targeting_rules[index]?.pattern?.message}
                      </TargetingRuleError>
                    )}

                  {targetingRuleFields.length !== index + 1 && (
                    <TargetingRuleSeparator
                      separator={t('common:site-settings.targeting-tracking.rule-separator-or')}
                    />
                  )}
                </React.Fragment>
              );
            })}

            <HelpLinkContainer>
              <Icon
                size={16}
                icon="info"
                color={tokens.color.neutral_500}
                css={css`
                  margin: 0 ${tokens.spacer.x2};
                `}
              />
              <LegacyLink
                href={`${helpHotjarUrl}${MATCHING_OPTIONS_LINK}`}
                target="_blank"
                onClick={() => {
                  trackClick('site.settings_targeting_matching-options-learn-more-link');
                }}
                rel="noreferrer"
              >
                {t('common:site-settings.targeting-tracking.matching-options-help')}
              </LegacyLink>
            </HelpLinkContainer>
          </TargetingRulesContainer>
        )}

        {isErrorCapturingFFEnabled && (
          <>
            <Separator />

            <Label htmlFor="session_capture_console_consent">
              {t('common:site-settings.targeting-tracking.error-and-console-tracking')}
              {!planAllowsErrorCapturing ? (
                <Badge
                  css={css`
                    margin: 0 0 0 ${tokens.spacer.x1};
                  `}
                  kind="custom"
                  size="s"
                  color={tokens.color.azure}
                >
                  {t('common:site-settings.targeting-tracking.scale')}
                </Badge>
              ) : (
                <>
                  {' '}
                  {site?.effectivePlan === 'business' ? (
                    <BasePopover
                      title={t('common:site-settings.targeting-tracking.scale-trial-popover-title')}
                      content={
                        <InternalPopover>
                          {t('common:site-settings.targeting-tracking.scale-trial-popover-content')}
                        </InternalPopover>
                      }
                      icon="info"
                      withCloseButton={false}
                      trigger="mouseenter focus"
                    >
                      <Badge
                        css={css`
                          margin: 0 0 0 ${tokens.spacer.x1};
                        `}
                        kind="custom"
                        size="s"
                        color={tokens.BRAND_COLORS.ember_700}
                      >
                        {t('common:site-settings.targeting-tracking.scale-trial')}
                      </Badge>
                    </BasePopover>
                  ) : null}
                </>
              )}
              <Description
                css={css`
                  margin: 0 0 ${tokens.spacer.x3};
                `}
              >
                {t(
                  'common:site-settings.targeting-tracking.error-and-console-tracking-description',
                )}
              </Description>
            </Label>

            <Controller
              control={control}
              id="session_capture_console_consent"
              name="session_capture_console_consent"
              render={({ onChange, value }) => (
                <Checkbox
                  partial={false}
                  checked={value}
                  onChange={(e) =>
                    planAllowsErrorCapturing
                      ? onChange(e.target.checked)
                      : upsell('site.console_tracking')
                  }
                  disabled={!hasSessionSettingsUpdatePermission}
                  labelCss={css`
                    color: ${tokens.color.text_default};
                  `}
                >
                  <Trans
                    i18nKey="common:site-settings.targeting-tracking.capture-errors"
                    components={[
                      <Link
                        kind="inline"
                        isExternal
                        href={`${helpHotjarUrl}/articles/10434365668247`}
                        css={css`
                          color: inherit !important;
                        `}
                      >
                        JavaScript errors and console logs
                      </Link>,
                    ]}
                  />
                </Checkbox>
              )}
            />
          </>
        )}

        <Separator />

        <Label htmlFor="state_change_listen_mode">
          {t('common:site-settings.targeting-tracking.url-changes')}
          <Description>
            <Trans i18nKey="common:site-settings.targeting-tracking.url-changes-description">
              This setting is only relevant for single page apps.
              <LegacyLink
                href={`${helpHotjarUrl}${TRACKING_URL_CHANGES_LINK}`}
                target="_blank"
                rel="noreferrer"
              >
                Learn more
              </LegacyLink>
              .
            </Trans>
          </Description>
        </Label>

        <Select
          id="state_change_listen_mode"
          name="state_change_listen_mode"
          ref={register({ required: true })}
          disabled={!hasSessionSettingsUpdatePermission}
          containerCss={InputsCss}
        >
          {urlChangesOptions.map((mode) => (
            <option value={mode} key={mode}>
              {t(`common:site-settings.targeting-tracking.url-changes-options.${mode}`)}
            </option>
          ))}
        </Select>

        <Label htmlFor="add-iframe-input">
          {t('common:site-settings.targeting-tracking.allowed-iframe-domains')}
          <Description>
            <Trans i18nKey="common:site-settings.targeting-tracking.allowed-iframe-domains-description">
              For security reasons, iframes are blocked from loading in recordings. Any iframes you
              wish to see during playback, need to be specified below and
              <LegacyLink
                href={`${helpHotjarUrl}${ALLOWED_IFRAMES_DOMAINS_LINK}`}
                target="_blank"
                rel="noreferrer"
              >
                manually tagged on your site
              </LegacyLink>
              .
            </Trans>
          </Description>
        </Label>

        <DomainInputContainer>
          <Input
            id="add-iframe-input"
            placeholder={t('common:site-settings.targeting-tracking.add-iframe-input-placeholder')}
            errorMessage={t(
              'common:site-settings.targeting-tracking.add-iframe-input-error-message',
            )}
            isError={addDomainError}
            disabled={!hasSessionSettingsUpdatePermission}
            containerCss={css`
              margin: ${tokens.spacer.x1} ${tokens.spacer.x1} ${tokens.spacer.x2} 0;
            `}
            onChange={onDomainToAddChange}
            onBlur={verifyDomainToAdd}
            value={domainToAdd}
            onKeyPress={onDomainToAddKeyPress}
          />
          <Button
            isDisabled={
              !hasSessionSettingsUpdatePermission ||
              isSubmitting ||
              !canSubmit ||
              addDomainError ||
              domainToAdd === ''
            }
            ref={addDomainButtonRef}
            onClick={onAddDomain}
            css={css`
              margin-top: ${tokens.spacer.x1};
            `}
          >
            {t('common:site-settings.targeting-tracking.add-domain')}
          </Button>
        </DomainInputContainer>

        <div>
          {domainsFields.map((domain, index) => {
            return (
              <Pillar
                key={domain.id}
                onDelete={() => {
                  domainsRemove(index);
                }}
                disabled={!hasSessionSettingsUpdatePermission}
              >
                {domain.url}
              </Pillar>
            );
          })}
        </div>

        <Tooltip
          enabled={!hasSessionSettingsUpdatePermission}
          content={t('common:site-settings.no-edit-access') as string}
          placement="right"
        >
          <div
            css={css`
              display: inline-block;
              margin-top: ${tokens.spacer.x2};
            `}
          >
            <Button
              type="submit"
              isDisabled={
                !hasSessionSettingsUpdatePermission || isSubmitting || !isDirty || !canSubmit
              }
              isLoading={!canSubmit}
              loadingText={t('common:site-settings.saving-changes')}
            >
              {t('common:site-settings.save-changes')}
            </Button>
          </div>
        </Tooltip>
      </Section>
    </form>
  );
};
